import { type SchedulingErrorDetail, SchedulingErrorDetailSchema } from "@emr/contracts";
import { z } from "zod";

/**
 * A scheduling request the API refused. The message is the API's PHI-free text and safe to
 * show. `code` is the API error code (ACCESS_DENIED, CONFLICT, VALIDATION_FAILED, ...) and
 * `detail` names the exact reason when a screen must react to it rather than read prose.
 */
export class SchedulingRequestError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly detail: SchedulingErrorDetail | undefined;

  public constructor(
    message: string,
    options: {
      readonly status: number;
      readonly code?: string | undefined;
      readonly detail?: SchedulingErrorDetail | undefined;
    },
  ) {
    super(message);
    this.name = "SchedulingRequestError";
    this.status = options.status;
    this.code = options.code;
    this.detail = options.detail;
  }
}

const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1).max(100).optional().catch(undefined),
    message: z.string().min(1).max(1_000).optional().catch(undefined),
    detail: SchedulingErrorDetailSchema.optional().catch(undefined),
  }),
});

/** Reads the API's `{error: {code, message, detail}}` envelope; unknown details are dropped. */
export async function schedulingRequestError(
  response: Response,
  fallbackMessage: string,
): Promise<SchedulingRequestError> {
  const envelope = ErrorEnvelopeSchema.safeParse(await response.json().catch(() => null));
  const error = envelope.success ? envelope.data.error : undefined;
  return new SchedulingRequestError(error?.message ?? fallbackMessage, {
    status: response.status,
    code: error?.code,
    detail: error?.detail,
  });
}

export function schedulingErrorDetail(error: unknown): SchedulingErrorDetail | undefined {
  return error instanceof SchedulingRequestError ? error.detail : undefined;
}

/** The signed-in role may not make this change (403). */
export function isSchedulingPermissionError(error: unknown): boolean {
  return error instanceof SchedulingRequestError && error.status === 403;
}

/**
 * Someone saved the clinic settings or this physician's calendar first: reload, then re-apply
 * the edit. Never retry the same request.
 */
export function isStaleSchedulingSave(error: unknown): boolean {
  const detail = schedulingErrorDetail(error);
  return detail === "scheduling_settings_changed" || detail === "provider_availability_changed";
}
